import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Badge,
  Form,
  Button,
  InputGroup,
  Spinner,
  FormCheck,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import moment from "moment";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as Yup from "yup";
import DailyReservationsList from "./DailyReservationsList";
import { toPesoAmount, isValidPhoneNumber } from "../../helpers";
import { getIsTableFullyBooked } from "./reservationSlots";
import PillButton from "./PillButton";
import { isReservationV2Enabled } from "../../helpers/featureFlags";

const MySwal = withReactContent(Swal);

const CELEBRATION_OPTIONS = [
  "Birthday",
  "Anniversary",
  "Graduation",
  "Engagement",
  "Reunion",
  "Promotion",
  "Baby Shower",
  "Bridal Shower",
  "Wedding",
];

const ALLERGY_OPTIONS = [
  "Peanuts",
  "Lactose Intolerant",
  "Mushrooms",
  "Shellfish",
  "Soy",
  "Eggs",
  "Wheat",
];

const PREFERENCE_OPTIONS = [
  "Diabetic",
  "Vegetarian",
  "Pescatarian",
  "Kosher",
  "Pregnant",
];

const reservationSchema = Yup.object({
  reservation_time: Yup.string().required(),
  table: Yup.string().required(),
  experience: Yup.string().required(),
  firstName: Yup.string().trim().min(1).required(),
  lastName: Yup.string().trim().min(1).required(),
  email: Yup.string().email().min(1).required(),
  contact: Yup.string()
    .min(1)
    .required()
    .test(
      "valid-phone",
      "Please enter a valid phone number",
      (value) => !value || isValidPhoneNumber(value),
    ),
});

const isFieldInvalid = (field, value) =>
  Boolean(value) && !Yup.reach(reservationSchema, field).isValidSync(value);

const getCheckoutDomain = () => {
  if (import.meta.env.VITE_APP_CHECKOUT_CART_DOMAIN) {
    return import.meta.env.VITE_APP_CHECKOUT_CART_DOMAIN;
  }
  const hostname = window?.location?.hostname || "";
  if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
    return "http://localhost:3000";
  }
  if (
    hostname.includes("app.stg") ||
    hostname.includes(".stg.") ||
    hostname.includes("-stg") ||
    import.meta.env.VITE_APP_API_URL?.includes("stg")
  ) {
    return "https://payments.stg.pickup.ph";
  }
  return "https://payments.pickup.ph";
};

const getCheckoutPaymentUrl = (reservation) => {
  if (!reservation) return "";
  if (reservation.payment_link) return reservation.payment_link;

  const cartId =
    (typeof reservation.cart === "object"
      ? reservation.cart?._id
      : reservation.cart) || reservation.cart_id;

  const rtId =
    typeof reservation.reservation_transaction === "object"
      ? reservation.reservation_transaction?._id
      : reservation.reservation_transaction;

  if (!cartId && !rtId) return "";

  const baseDomain = getCheckoutDomain();
  let cleanDomain = baseDomain.replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(cleanDomain)) {
    cleanDomain = `https://${cleanDomain}`;
  }

  if (cartId) {
    return `${cleanDomain}/checkout/${cartId}`;
  }
  return `${cleanDomain}/reservation/update/${rtId}`;
};

const SmartReservationForm = ({
  values,
  isAdding,
  isDeleting,
  selected_reservation,
  allTimeSlots,
  tablesForSelectedTime,
  uniqueExperiences,
  selectedTable,
  monthlyReservations,
  withPaymentLink,
  RESERVATION_COLOR,
  loadingTables,
  handlers,
  inputActions,
}) => {
  const {
    getTableUniqueExperience,
    handleDateChange,
    handlePartySizeChange,
    handlePillClick,
    handleCreateReservation,
    handleCancelReservation,
    fetchCalendarReservations,
    formatDateForInput,
    dayHeaderContent,
    handleReservationSelect,
    handleDietaryPillClick,
    handleUpdateDietaryCount,
    handleAddCustomDietaryItem,
    handleCelebrationPillClick,
    handlewithPaymentLinkChange,
    handleExclusiveExperiencePillClick,
    handleAddCustomCelebrationItem,
    handleResendEmail,
    handleAddAdditional,
    handleRemoveAdditional,
  } = handlers;

  // Calendar modal state
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showDietary, setShowDietary] = useState(false);
  const [showExclusiveExperiences, setShowExclusiveExperiences] =
    useState(false);
  const [showCelebrations, setShowCelebrations] = useState(false);
  const [showAdditionals, setShowAdditionals] = useState(
    Boolean(values.additionals && values.additionals.length > 0),
  );
  const [additionalLabel, setAdditionalLabel] = useState("");
  const [additionalAmount, setAdditionalAmount] = useState("");
  const additionalLabelRef = useRef(null);

  // Payment link state & copy feedback
  const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Date editing state (edit mode only)
  const [showDateEditor, setShowDateEditor] = useState(false);
  const originalDate = selected_reservation
    ? moment(selected_reservation.reservation_date).format("YYYY-MM-DD")
    : null;
  const dateHasChanged =
    selected_reservation && values.reservation_date !== originalDate;

  // Handle calendar date click
  const handleCalendarDateClick = (arg) => {
    arg.jsEvent.preventDefault();
    setShowCalendarModal(false);
    handleDateChange({ target: { value: arg.dateStr } });
    handlers.refetchReservationByDate(arg.dateStr);
  };

  const basePillStyle = {
    margin: "0.25rem",
    padding: "0.5rem 0.75rem",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "500",
    transition: "all 0.2s ease",
    border: "none",
    borderRadius: "50rem",
  };

  const pillStyle = {
    ...basePillStyle,
    backgroundColor: "#f0f0f0",
    color: "#555",
  };
  const activePillStyle = {
    ...basePillStyle,
    backgroundColor: RESERVATION_COLOR,
    color: "#ffffff",
  };

  // moved inside since both consumer pass same validation
  const isFormInvalid = !reservationSchema.isValidSync(values);

  const handleAutoFillGuestInfo = () => {
    const randomString = Math.random().toString(36).substring(2, 10);
    inputActions.onChange({
      target: { name: "contact", value: "+63 911 111 1111" },
    });
    inputActions.onChange({
      target: { name: "email", value: `${randomString}@guest.com` },
    });
  };

  const selectedExperienceDetails = useMemo(
    () => uniqueExperiences.find((exp) => exp._id === values.experience),
    [uniqueExperiences, values.experience],
  );

  const isExclusiveExperience = Boolean(selectedExperienceDetails?.isExclusive);

  const isV2 = isReservationV2Enabled();
  // "Custom price" is the NEW TOTAL for the booking (create or edit) — the
  // same semantics as api's customAmount/customTotal, not a delta. Any
  // non-empty, parseable value overrides the computed total outright.
  const hasCustomAmount =
    isV2 &&
    values.customAmount !== undefined &&
    values.customAmount !== "" &&
    !isNaN(Number(values.customAmount));

  const isPending =
    selected_reservation?.payment?.status === "pending" ||
    selected_reservation?.payment?.status === "failed";
  const isPaidStatus = selected_reservation?.payment?.status === "paid";
  const rawPaid = Number(selected_reservation?.payment?.amount || 0);

  const paidAmount = useMemo(() => {
    if (isPending || !isPaidStatus) return 0;
    return !isNaN(rawPaid) && rawPaid > 0 ? rawPaid : 0;
  }, [isPending, isPaidStatus, rawPaid]);

  const isPaid = isPaidStatus && paidAmount > 0;

  const experienceSubtotal =
    (selectedExperienceDetails?.price || 0) * (values.party_size || 0);
  const additionalsSubtotal = (values.additionals || []).reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );
  const computedTotalAmount = experienceSubtotal + additionalsSubtotal;
  const totalAmount = hasCustomAmount
    ? Number(values.customAmount)
    : computedTotalAmount;

  const unpaidTotal = useMemo(() => {
    return Math.max(0, totalAmount - paidAmount);
  }, [totalAmount, paidAmount]);

  const paymentLink = useMemo(
    () => getCheckoutPaymentUrl(selected_reservation),
    [selected_reservation],
  );

  const handleCopyPaymentLink = async () => {
    if (handlers?.handleCopyPaymentLink) {
      handlers.handleCopyPaymentLink(paymentLink);
      return;
    }

    if (!paymentLink) {
      MySwal.fire({
        toast: true,
        icon: "error",
        title: "No payment link available",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    let success = false;
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(paymentLink);
        success = true;
      } catch (err) {
        console.warn(
          "navigator.clipboard.writeText failed, attempting fallback",
          err,
        );
      }
    }

    if (!success) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = paymentLink;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textArea);
      } catch (err) {
        console.error("Fallback clipboard copy failed:", err);
      }
    }

    if (success) {
      setCopiedPaymentLink(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedPaymentLink(false);
      }, 2500);

      MySwal.fire({
        toast: true,
        icon: "success",
        title: "Payment link copied!",
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      MySwal.fire({
        icon: "info",
        title: "Payment Link",
        text: paymentLink,
        confirmButtonText: "Close",
      });
    }
  };

  const showOverBookingNote = useMemo(() => {
    const isSelectedTimeSlotFull =
      allTimeSlots?.find?.((ats) => ats.value === values.reservation_time)
        ?.isFullyBooked ?? false;
    const isSelectedTableFull =
      selectedTable &&
      !selectedTable.time_slots?.includes(values.reservation_time);
    return isSelectedTimeSlotFull || isSelectedTableFull;
  }, [allTimeSlots, selectedTable, values.reservation_time]);

  const filteredAllTimeSlots = useMemo(() => {
    return values.allowOverBooking !== false
      ? allTimeSlots
      : allTimeSlots.filter((ts) => !ts.isFullyBooked);
  }, [values.allowOverBooking, allTimeSlots]);

  const filteredTablesForSelectedTime = useMemo(() => {
    if (values.allowOverBooking !== false) {
      return tablesForSelectedTime.filter(
        (table) => getTableUniqueExperience(table.item._id).length > 0,
      );
    }
    return tablesForSelectedTime.filter((table) =>
      table.time_slots.includes(values.reservation_time),
    );
  }, [
    values.allowOverBooking,
    values.reservation_time,
    tablesForSelectedTime,
    getTableUniqueExperience,
  ]);

  // Auto-select experience if only one is available
  useEffect(() => {
    const viewableExperiences = uniqueExperiences.filter(
      (exp) => !exp.isExclusive,
    );
    if (
      viewableExperiences.length === 1 &&
      !values.experience &&
      values.table
    ) {
      handlePillClick("experience", viewableExperiences[0]._id);
    }
  }, [uniqueExperiences, values.experience, values.table, handlePillClick]);

  // Initialize toggle states when selected_reservation changes (modal open / reservation switch)
  useEffect(() => {
    setShowDietary(Boolean(values.dietary && values.dietary.length > 0));
    setShowCelebrations(
      Boolean(values.celebration && values.celebration.length > 0),
    );
    setShowAdditionals(
      Boolean(values.additionals && values.additionals.length > 0),
    );
    setShowExclusiveExperiences(
      Boolean(selectedExperienceDetails?.isExclusive),
    );
    setShowDateEditor(false);
  }, [selected_reservation]);

  // Auto-expand sections if items are added
  useEffect(() => {
    if (values.additionals && values.additionals.length > 0) {
      setShowAdditionals(true);
    }
  }, [values.additionals]);

  useEffect(() => {
    if (values.dietary && values.dietary.length > 0) {
      setShowDietary(true);
    }
  }, [values.dietary]);

  useEffect(() => {
    if (values.celebration && values.celebration.length > 0) {
      setShowCelebrations(true);
    }
  }, [values.celebration]);

  useEffect(() => {
    if (selectedExperienceDetails?.isExclusive) {
      setShowExclusiveExperiences(true);
      if (values.party_size !== 1) {
        handlePartySizeChange(1 - values.party_size);
      }
    }
  }, [selectedExperienceDetails]);

  return (
    <>
      <style>
        {`
         .swal-cancel-btn {
            background-color: white !important;
            color: ${RESERVATION_COLOR} !important;
            border: 1px solid ${RESERVATION_COLOR} !important;
          }
          .custom-switch .custom-control-label {
              cursor: pointer;
          }
          .custom-switch .custom-control-label::before {
              width: 2.75rem !important;
              height: 1.5rem !important;
              border-radius: 1rem !important;
              top: 0.15rem;
              background-color: #e4e6ef !important;
              border: 1px solid #d5d7e0 !important;
              transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
          }
          .custom-switch .custom-control-label::after {
              width: calc(1.5rem - 4px) !important;
              height: calc(1.5rem - 4px) !important;
              border-radius: 50% !important;
              background-color: #ffffff !important;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
              top: calc(0.15rem + 2px);
              transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out !important;
          }
          .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
              background-color: ${RESERVATION_COLOR} !important;
              border-color: ${RESERVATION_COLOR} !important;
          }
          .custom-switch .custom-control-input:checked ~ .custom-control-label::after {
              background-color: #ffffff !important;
              transform: translateX(1.25rem) !important;
          }
          .custom-switch .custom-control-input:focus ~ .custom-control-label::before {
              box-shadow: 0 0 0 0.2rem ${RESERVATION_COLOR}30 !important;
          }
          .custom-switch .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
              border-color: #bcc0cc !important;
              box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.2) !important;
          }
          .custom-switch {
              padding-left: 3.5rem !important;
              min-height: 1.8rem;
          }
          .bg-light-orange {
            background-color: #fa903e20; /* Orange with low opacity */
          }
          .text-orange {
            color: #fa903e !important;
          }
          .purple-checkbox.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
              background-color: ${RESERVATION_COLOR} !important;
              border-color: ${RESERVATION_COLOR} !important;
          }
          .purple-checkbox.custom-checkbox .custom-control-input:focus ~ .custom-control-label::before {
              box-shadow: 0 0 0 0.2rem ${RESERVATION_COLOR}40 !important;
          }
          .allow-override.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
              background-color: #FFA800 !important;
              border-color: #FFA800 !important;
          }
          .allow-override.custom-checkbox .custom-control-input:focus ~ .custom-control-label::before {
              box-shadow: 0 0 0 0.2rem #ffc10740 !important;
          }
          .allow-override.custom-checkbox .custom-control-label::before {
            width: 1.25rem;
            height: 1.25rem;
          }
          .allow-override.custom-checkbox .custom-control-label {
              font-size: 1.25rem;
              color: #212121;
              font-weight: 700;
          }
          .allow-override.custom-checkbox .custom-control-label::after {
            width: 1.25rem;
            height: 1.25rem;
          }
          .summary-box {
              background-color: ${RESERVATION_COLOR}08;
              border: 1px dashed ${RESERVATION_COLOR}40;
              border-radius: 12px;
              padding: 20px;
          }
          .date-move-card {
              background: linear-gradient(135deg, ${RESERVATION_COLOR}10 0%, ${RESERVATION_COLOR}06 100%);
              border: 1.5px solid ${RESERVATION_COLOR}30;
              border-radius: 12px;
              padding: 14px 16px;
              transition: all 0.2s ease;
          }
          .date-move-toggle {
              background: none;
              border: none;
              cursor: pointer;
              padding: 0;
              color: ${RESERVATION_COLOR};
              font-weight: 700;
              font-size: 0.85rem;
              display: flex;
              align-items: center;
              gap: 6px;
              transition: opacity 0.15s;
          }
          .date-move-toggle:hover { opacity: 0.75; }
          .date-changed-badge {
              background-color: #fff3cd;
              color: #856404;
              border: 1px solid #ffc10740;
              border-radius: 20px;
              padding: 3px 10px;
              font-size: 0.75rem;
              font-weight: 700;
              display: inline-flex;
              align-items: center;
              gap: 5px;
          }
          `}
      </style>

      <div style={{ position: "relative" }}>
        {(isAdding || isDeleting) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <Spinner animation="border" style={{ color: RESERVATION_COLOR }} />
          </div>
        )}

        <Row>
          {/* Left Column: Quick Selectors */}
          <Col lg={6}>
            {/* Date & Time Section */}
            <div className="mb-8">
              <div
                className="d-flex align-items-center mb-5 pl-2"
                style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
              >
                <h5 className="text-dark font-weight-boldest m-0 pl-4">
                  <i
                    className="fas fa-calendar-check mr-3"
                    style={{ color: RESERVATION_COLOR }}
                  ></i>
                  Date & Time
                </h5>
              </div>

              <Row>
                {/* Date Selector */}
                <Col md={selected_reservation ? 12 : 6}>
                  {selected_reservation ? (
                    /* ── EDIT MODE: Current Booking Date Card ── */
                    <div className="mb-4">
                      <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-3">
                        Reservation Date
                      </Form.Label>

                      <div className="date-move-card">
                        {/* Current date display */}
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div
                              className="btn btn-icon btn-sm mr-3 no-hover"
                              style={{
                                backgroundColor: `${RESERVATION_COLOR}18`,
                                width: "38px",
                                height: "38px",
                                minWidth: "38px",
                              }}
                            >
                              <i
                                className="fas fa-calendar-day"
                                style={{ color: RESERVATION_COLOR }}
                              ></i>
                            </div>
                            <div>
                              <div
                                className="font-weight-boldest text-dark"
                                style={{ fontSize: "1rem", lineHeight: 1.2 }}
                              >
                                {moment(values.reservation_date).format(
                                  "dddd, MMMM D",
                                )}
                              </div>
                            </div>
                          </div>

                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "10px" }}
                          >
                            {dateHasChanged && (
                              <span className="date-changed-badge">
                                <i className="fas fa-exchange-alt"></i>
                                Date changed
                              </span>
                            )}
                            <button
                              type="button"
                              className="date-move-toggle"
                              onClick={() => setShowDateEditor(!showDateEditor)}
                            >
                              <i
                                className={`fas fa-${
                                  showDateEditor ? "times" : "pencil-alt"
                                }`}
                              ></i>
                              {showDateEditor ? "Cancel" : "Move"}
                            </button>
                          </div>
                        </div>

                        {/* Expandable date editor */}
                        {showDateEditor && (
                          <div
                            className="mt-4 pt-4 animate__animated animate__fadeIn"
                            style={{
                              borderTop: `1px dashed ${RESERVATION_COLOR}30`,
                            }}
                          >
                            {/* Informational notice */}
                            <div
                              className="d-flex align-items-start mb-3 p-3 rounded-lg"
                              style={{
                                backgroundColor: "#e8f4fd",
                                border: "1px solid #bee3f840",
                              }}
                            >
                              <i
                                className="fas fa-info-circle mr-3 mt-1"
                                style={{
                                  color: RESERVATION_COLOR,
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }}
                              ></i>
                              <div
                                className="text-dark-75"
                                style={{ fontSize: "0.82rem", lineHeight: 1.5 }}
                              >
                                Your current slot, table, and experience
                                selections will be kept. Note that availability
                                may differ on the new date.
                              </div>
                            </div>

                            <Form.Control
                              type="date"
                              className="form-control-solid h-auto py-3 px-4 mb-3"
                              value={values.reservation_date}
                              onChange={(e) => {
                                handleDateChange(e);
                              }}
                              min={moment().format("YYYY-MM-DD")}
                            />

                            <button
                              type="button"
                              className="w-100 d-flex align-items-center justify-content-center btn btn-sm btn-light-info font-weight-boldest no-hover"
                              onClick={() => setShowCalendarModal(true)}
                            >
                              <i className="fas fa-calendar-alt mr-2"></i>
                              <span className="font-size-lg">
                                Pick from Calendar
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* ── CREATE MODE: Standard date input ── */
                    <Form.Group className="mb-4">
                      <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-3">
                        Select Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        className="form-control-solid h-auto py-3 px-4"
                        value={values.reservation_date}
                        onChange={handleDateChange}
                        min={moment().format("YYYY-MM-DD")}
                      />

                      {/* Calendar View Button */}
                      <button
                        type="button"
                        className="mt-3 w-100 d-flex align-items-center justify-content-center btn btn-sm btn-light-info font-weight-boldest no-hover"
                        onClick={() => setShowCalendarModal(true)}
                      >
                        <i className="fas fa-calendar-alt mr-2"></i>
                        <span className="font-size-lg">
                          View Calendar & Reservations
                        </span>
                      </button>
                    </Form.Group>
                  )}
                </Col>

                {/* Party Size */}
                <Col md={6} className="mb-4">
                  <Form.Group>
                    <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-3">
                      Party Size
                    </Form.Label>
                    <InputGroup
                      className="bg-light rounded-lg px-2 d-flex align-items-center"
                      style={{
                        maxWidth: "180px",
                        border: "1px solid #ebedf3",
                        height: "48px",
                      }}
                    >
                      <Button
                        variant="link"
                        className="btn btn-icon btn-light-primary btn-sm rounded-lg d-flex align-items-center justify-content-center"
                        onClick={() => handlePartySizeChange(-1)}
                        disabled={
                          loadingTables ||
                          values.party_size <= 1 ||
                          isExclusiveExperience
                        }
                        style={{ width: "32px", height: "32px" }}
                      >
                        <i className="fas fa-minus text-primary"></i>
                      </Button>
                      <Form.Control
                        type="text"
                        className="text-center font-weight-boldest border-0 bg-transparent px-2 h-auto"
                        value={`${values.party_size} guest${
                          values.party_size > 1 ? "s" : ""
                        }`}
                        readOnly
                        style={{ fontSize: "1rem", boxShadow: "none" }}
                      />
                      {isExclusiveExperience ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="party-size-exclusive-tooltip">
                              Party size is fixed to 1 for exclusive experiences
                            </Tooltip>
                          }
                        >
                          <span
                            className="d-inline-block"
                            style={{ cursor: "not-allowed" }}
                          >
                            <Button
                              variant="link"
                              className="btn btn-icon btn-light-primary btn-sm rounded-lg d-flex align-items-center justify-content-center"
                              disabled={true}
                              style={{
                                width: "32px",
                                height: "32px",
                                pointerEvents: "none",
                              }}
                            >
                              <i className="fas fa-plus text-primary"></i>
                            </Button>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <Button
                          variant="link"
                          className="btn btn-icon btn-light-primary btn-sm rounded-lg d-flex align-items-center justify-content-center"
                          onClick={() => handlePartySizeChange(1)}
                          disabled={loadingTables}
                          style={{ width: "32px", height: "32px" }}
                        >
                          <i className="fas fa-plus text-primary"></i>
                        </Button>
                      )}
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Time Slots */}
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center mb-4 mt-5">
                  <div
                    className="d-flex flex-row align-items-center pl-2"
                    style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
                  >
                    <h5 className="text-dark font-weight-boldest m-0 pl-4 mr-2">
                      <i
                        className="far fa-clock mr-3"
                        style={{ color: RESERVATION_COLOR }}
                      ></i>
                      Available Slots
                    </h5>
                    {loadingTables && (
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ color: RESERVATION_COLOR }}
                        className="ml-1"
                      />
                    )}
                  </div>
                  {isV2 && (
                    <FormCheck
                      custom
                      type="checkbox"
                      id="overrideTableCapacity"
                      checked={Boolean(values.overrideTableCapacity)}
                      onChange={(e) =>
                        handlePillClick(
                          "overrideTableCapacity",
                          e.target.checked,
                        )
                      }
                      label="Override table capacity"
                      className="allow-override"
                    />
                  )}
                </div>
                {isV2 && values.overrideTableCapacity && (
                  <div className="text-warning font-size-sm mb-2">
                    <i className="fas fa-exclamation-triangle mr-1" />
                    Party size can be set outside this table's normal
                    minimum/maximum.
                  </div>
                )}

                <div className="d-flex flex-wrap">
                  {filteredAllTimeSlots.length > 0 ? (
                    filteredAllTimeSlots.map((slot) => (
                      <PillButton
                        key={slot.value}
                        active={values.reservation_time === slot.value}
                        disabled={loadingTables}
                        text={moment(slot.value, "HH:mm").format("h:mm A")}
                        warn={slot.isFullyBooked}
                        className="text-center px-4 py-2 mr-2 mb-2"
                        style={{
                          minWidth: "100px",
                          transition: "0.2s ease",
                        }}
                        onClick={() => {
                          const isTableFullyBooked = getIsTableFullyBooked(
                            values.table,
                            slot.value,
                            tablesForSelectedTime,
                          );
                          if (isTableFullyBooked) {
                            handlePillClick("table", {});
                          }
                          handlePillClick("reservation_time", slot.value);
                        }}
                        solidActive
                        noIcon
                      />
                    ))
                  ) : (
                    <div className="w-100 p-6 bg-light-o-50 rounded-lg border border-dashed d-flex align-items-center">
                      <i className="fas fa-info-circle text-muted mr-3 font-size-h4"></i>
                      <span className="text-muted font-weight-bold">
                        No available times for this date. Try a different date
                        or party size.
                      </span>
                    </div>
                  )}
                </div>
              </Form.Group>
            </div>

            {/* Tables Section */}
            {values.reservation_time && (
              <div className="mb-8 animate__animated animate__fadeIn">
                <div
                  className="d-flex flex-row align-items-center mb-4 pl-2"
                  style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
                >
                  <h5 className="text-dark font-weight-boldest m-0 pl-4 mr-2">
                    <i
                      className="fas fa-chair mr-3"
                      style={{ color: RESERVATION_COLOR }}
                    ></i>
                    Available Tables
                  </h5>
                  {loadingTables && (
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ color: RESERVATION_COLOR }}
                      className="ml-1"
                    />
                  )}
                </div>

                {filteredTablesForSelectedTime.length > 0 ? (
                  <Row>
                    {filteredTablesForSelectedTime.map((table) => {
                      const tableData = table?.item || table || {};
                      const name =
                        tableData.name || table.name || "Unnamed Table";
                      return (
                        <Col md={5} key={table.item._id} className="mb-4">
                          <PillButton
                            active={values.table === table.item._id}
                            iconClassName="fas fa-chair"
                            text={name}
                            warn={
                              !table.time_slots.includes(
                                values.reservation_time,
                              )
                            }
                            warnText="Fully Booked"
                            disabled={loadingTables}
                            style={{ width: "100%" }}
                            onClick={() => handlePillClick("table", table.item)}
                          />
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div className="alert alert-custom alert-light-warning border-0 py-4 px-6 d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle text-warning font-size-h3 mr-4"></i>
                    <div className="alert-text font-weight-bold text-dark-75">
                      No tables available for{" "}
                      {moment(values.reservation_time, "HH:mm").format(
                        "h:mm A",
                      )}
                      . Please select a different time.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Experience Section */}
            {values.table && uniqueExperiences.length > 0 && (
              <div className="mb-6 animate__animated animate__fadeIn">
                <div
                  className="d-flex flex-row align-items-center mb-5 pl-2"
                  style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
                >
                  <h5 className="text-dark font-weight-boldest m-0 pl-4 mr-2">
                    <i
                      className="fas fa-utensils mr-3"
                      style={{ color: RESERVATION_COLOR }}
                    ></i>
                    Dining Experience
                  </h5>
                  {loadingTables && (
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ color: RESERVATION_COLOR }}
                      className="ml-1"
                    />
                  )}
                </div>

                <Row>
                  {uniqueExperiences
                    .filter((exp) => !exp.isExclusive)
                    .map((exp) => (
                      <Col md={5} key={exp._id} className="mb-4">
                        <PillButton
                          active={values.experience === exp._id}
                          iconClassName="fas fa-utensils"
                          text={exp.name}
                          disabled={loadingTables}
                          style={{ width: "100%" }}
                          onClick={() => handlePillClick("experience", exp._id)}
                        />
                      </Col>
                    ))}
                </Row>
              </div>
            )}

            {/* Additionals Section */}
            <div className="mb-6 animate__animated animate__fadeIn">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div
                  className="d-flex flex-row align-items-center pl-2"
                  style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
                >
                  <h5 className="text-dark font-weight-boldest m-0 pl-4 mr-2">
                    <i
                      className="fas fa-plus-circle mr-3"
                      style={{ color: RESERVATION_COLOR }}
                    ></i>
                    Additionals
                  </h5>
                </div>
                {isPaid ? (
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Tooltip id="additionals-disabled-tooltip">
                        {(values.additionals || []).length > 0
                          ? "Additionals cannot be modified for paid reservations"
                          : "You cannot add additionals to an already paid reservation"}
                      </Tooltip>
                    }
                  >
                    <span
                      className="d-inline-block"
                      style={{ cursor: "not-allowed" }}
                    >
                      <Form.Check
                        type="switch"
                        id="additionals-switch"
                        label=""
                        checked={showAdditionals}
                        disabled={true}
                        style={{ pointerEvents: "none" }}
                        className="custom-switch"
                      />
                    </span>
                  </OverlayTrigger>
                ) : (
                  <Form.Check
                    type="switch"
                    id="additionals-switch"
                    label=""
                    checked={showAdditionals}
                    onChange={() => setShowAdditionals(!showAdditionals)}
                    className="custom-switch"
                  />
                )}
              </div>

              {showAdditionals && (
                <div className="animate__animated animate__fadeIn">
                  {isPaid && (
                    <div
                      className="d-flex align-items-center mb-3 p-3 rounded-lg"
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                        fontSize: "0.82rem",
                        color: "#6c757d",
                      }}
                    >
                      <i className="fas fa-lock mr-2 text-muted"></i>
                      Additionals cannot be modified because this reservation is
                      already paid.
                    </div>
                  )}
                  {!isPaid && (
                    <Row>
                      <Col md={7} sm={12}>
                        <Form.Group className="mb-4">
                          <div
                            className="d-flex justify-content-between align-items-center mb-2"
                            style={{ minHeight: "20px" }}
                          >
                            <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-0">
                              Item Name
                            </Form.Label>
                            <span
                              className="badge badge-light font-weight-boldest text-muted"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {additionalLabel.length}/100
                            </span>
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            ref={additionalLabelRef}
                            id="additionalLabel"
                            className="form-control-solid py-3 px-4"
                            style={{
                              resize: "none",
                              overflow: "hidden",
                              height: "48px",
                              minHeight: "48px",
                              lineHeight: "1.4",
                            }}
                            placeholder="e.g. Birthday Cake, Champagne"
                            value={additionalLabel}
                            maxLength={100}
                            onChange={(e) => {
                              setAdditionalLabel(e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height = `${Math.max(
                                48,
                                e.target.scrollHeight,
                              )}px`;
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (
                                  additionalLabel.trim() &&
                                  additionalAmount !== "" &&
                                  parseInt(additionalAmount, 10) > 0
                                ) {
                                  handleAddAdditional({
                                    label: additionalLabel.trim(),
                                    amount: parseInt(additionalAmount, 10),
                                  });
                                  setAdditionalLabel("");
                                  setAdditionalAmount("");
                                  if (additionalLabelRef.current) {
                                    additionalLabelRef.current.style.height =
                                      "48px";
                                  }
                                }
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={5} sm={12}>
                        <Form.Group className="mb-4">
                          <div
                            className="d-flex justify-content-between align-items-center mb-2"
                            style={{ minHeight: "20px" }}
                          >
                            <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-0">
                              Amount (₱)
                            </Form.Label>
                          </div>
                          <InputGroup style={{ height: "48px" }}>
                            <Form.Control
                              id="additionalAmount"
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              className="form-control-solid py-3 px-4"
                              style={{ height: "48px" }}
                              placeholder="0"
                              value={additionalAmount}
                              onChange={(e) => {
                                const v = e.target.value.replace(/[^0-9]/g, "");
                                if (
                                  v === "" ||
                                  (Number(v) >= 0 && Number(v) <= 999999)
                                ) {
                                  setAdditionalAmount(v);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (
                                  ["e", "E", "+", "-", ".", ","].includes(e.key)
                                ) {
                                  e.preventDefault();
                                  return;
                                }
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  if (
                                    additionalLabel.trim() &&
                                    additionalAmount !== "" &&
                                    parseInt(additionalAmount, 10) > 0
                                  ) {
                                    handleAddAdditional({
                                      label: additionalLabel.trim(),
                                      amount: parseInt(additionalAmount, 10),
                                    });
                                    setAdditionalLabel("");
                                    setAdditionalAmount("");
                                    if (additionalLabelRef.current) {
                                      additionalLabelRef.current.style.height =
                                        "48px";
                                    }
                                  }
                                }
                              }}
                            />
                            <InputGroup.Append>
                              <Button
                                id="addAdditionalBtn"
                                className="font-weight-boldest px-4 d-flex align-items-center justify-content-center"
                                style={{
                                  backgroundColor: RESERVATION_COLOR,
                                  borderColor: RESERVATION_COLOR,
                                  color: "#fff",
                                  height: "48px",
                                }}
                                disabled={
                                  !additionalLabel.trim() ||
                                  additionalAmount === "" ||
                                  parseInt(additionalAmount, 10) <= 0
                                }
                                onClick={() => {
                                  handleAddAdditional({
                                    label: additionalLabel.trim(),
                                    amount: parseInt(additionalAmount, 10),
                                  });
                                  setAdditionalLabel("");
                                  setAdditionalAmount("");
                                  if (additionalLabelRef.current) {
                                    additionalLabelRef.current.style.height =
                                      "48px";
                                  }
                                }}
                              >
                                <i className="fas fa-plus mr-1"></i> Add
                              </Button>
                            </InputGroup.Append>
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {/* Saved additionals list */}
                  {(values.additionals || []).length > 0 && (
                    <div className="mb-4">
                      {(values.additionals || []).map((item, index) => (
                        <div
                          key={`addon-${index}`}
                          className="d-flex justify-content-between align-items-center py-2 px-3 mb-2 rounded-lg"
                          style={{
                            backgroundColor: `${RESERVATION_COLOR}10`,
                            border: `1.5px solid ${RESERVATION_COLOR}25`,
                            minWidth: 0,
                          }}
                        >
                          <div
                            className="d-flex align-items-center flex-nowrap mr-2"
                            style={{ minWidth: 0, flex: 1 }}
                          >
                            <i
                              className="fas fa-check-circle mr-2 font-size-sm flex-shrink-0"
                              style={{ color: RESERVATION_COLOR }}
                            ></i>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-addon-${index}`}>
                                  {item.label}
                                </Tooltip>
                              }
                            >
                              <span
                                className="font-weight-bold text-dark font-size-sm text-truncate d-inline-block"
                                style={{
                                  maxWidth: "100%",
                                  cursor: "pointer",
                                }}
                                title={item.label}
                              >
                                {item.label}
                              </span>
                            </OverlayTrigger>
                          </div>
                          <div className="d-flex align-items-center flex-shrink-0 ml-2">
                            <span
                              className="font-weight-boldest font-size-sm flex-shrink-0"
                              style={{
                                color: RESERVATION_COLOR,
                              }}
                            >
                              {toPesoAmount(item.amount)}
                            </span>
                            {!isPaid && (
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-icon btn-sm py-0 px-2 ml-2 flex-shrink-0"
                                style={{
                                  width: "26px",
                                  height: "26px",
                                  borderRadius: "50%",
                                  lineHeight: 1,
                                }}
                                title="Remove additional"
                                onClick={() => handleRemoveAdditional(index)}
                              >
                                <i className="fas fa-times font-size-xs text-muted"></i>
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Col>

          {/* Right Column: Summary & Guest Info */}
          <Col lg={6}>
            <div className="sticky-top" style={{ top: "20px" }}>
              {/* Overbooking Note */}
              {showOverBookingNote && (
                <div className="alert alert-custom alert-light-warning border-warning shadow-sm mb-6 d-flex align-items-center animate__animated animate__fadeIn">
                  <span className="svg-icon svg-icon-warning svg-icon-2x mr-4">
                    <i className="fas fa-exclamation-triangle text-warning font-size-h2"></i>
                  </span>
                  <div className="alert-text font-weight-bold">
                    <h6 className="mb-1 text-warning font-weight-boldest">
                      Selection Fully Booked
                    </h6>
                    <span className="text-dark-75">
                      Your selection is fully booked. Proceeding may result in
                      overbooking.
                    </span>
                  </div>
                </div>
              )}

              {/* Booking Summary */}
              <DailyReservationsList
                reservations={monthlyReservations}
                selectedDate={values.reservation_date}
                RESERVATION_COLOR={RESERVATION_COLOR}
                onReservationClick={handleReservationSelect}
              />

              {/* Guest Information */}
              {values.experience && (
                <div className="mt-6 animate__animated animate__fadeIn">
                  <div
                    className="d-flex align-items-center justify-content-between mb-4 pl-2"
                    style={{ borderLeft: `4px solid ${RESERVATION_COLOR}` }}
                  >
                    <h6 className="text-dark font-weight-boldest m-0 pl-4">
                      Guest Information
                    </h6>
                    <Button
                      variant="light"
                      size="sm"
                      className="font-weight-boldest"
                      style={{ color: "#4338ca" }}
                      onClick={handleAutoFillGuestInfo}
                    >
                      <i
                        className="fas fa-bolt mr-2"
                        style={{ color: "#4338ca" }}
                      ></i>
                      Auto-fill Mobile & Email
                    </Button>
                  </div>

                  <Form>
                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-4">
                          <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                            First Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            className="form-control-solid h-auto py-3 px-4"
                            placeholder="First name"
                            value={values.firstName || ""}
                            onChange={(e) => inputActions.onChange(e)}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-4">
                          <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                            Last Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            className="form-control-solid h-auto py-3 px-4"
                            placeholder="Last name"
                            value={values.lastName || ""}
                            onChange={(e) => inputActions.onChange(e)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-4">
                      <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                        Email
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        className="form-control-solid h-auto py-3 px-4"
                        placeholder="email@example.com"
                        value={values.email || ""}
                        onChange={(e) => inputActions.onChange(e)}
                        isInvalid={isFieldInvalid("email", values.email)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-6">
                      <Form.Label className="font-weight-boldest text-uppercase font-size-xs text-muted mb-2">
                        Phone
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="contact"
                        className="form-control-solid h-auto py-3 px-4"
                        placeholder="+63 xxx xxx xxxx"
                        value={values.contact || ""}
                        onChange={(e) => inputActions.onChange(e)}
                        isInvalid={isFieldInvalid("contact", values.contact)}
                      />
                    </Form.Group>

                    {/* Dietary Notes Section */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="font-weight-boldest font-size-md mb-0">
                          Any dietary restrictions?
                        </Form.Label>
                        <Form.Check
                          type="switch"
                          id="dietary-switch"
                          label=""
                          checked={showDietary}
                          onChange={() => setShowDietary(!showDietary)}
                          className="custom-switch"
                        />
                      </div>
                      {showDietary && (
                        <div className="p-3 border rounded bg-light-light animate__animated animate__fadeIn">
                          <div className="mb-2">
                            <Form.Label as="small" className="text-muted">
                              Allergies
                            </Form.Label>
                            <div>
                              {ALLERGY_OPTIONS.map((option) => (
                                <Badge
                                  key={option}
                                  pill
                                  style={
                                    (values.dietary || []).some(
                                      (item) => item.label === option,
                                    )
                                      ? activePillStyle
                                      : pillStyle
                                  }
                                  onClick={() => handleDietaryPillClick(option)}
                                >
                                  {option}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Form.Label as="small" className="text-muted">
                              Preferences
                            </Form.Label>
                            <div>
                              {PREFERENCE_OPTIONS.map((option) => (
                                <Badge
                                  key={option}
                                  pill
                                  style={
                                    (values.dietary || []).some(
                                      (item) => item.label === option,
                                    )
                                      ? activePillStyle
                                      : pillStyle
                                  }
                                  onClick={() => handleDietaryPillClick(option)}
                                >
                                  {option}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <InputGroup className="mt-3">
                            <Form.Control
                              name="otherDietaryNote"
                              value={values.otherDietaryNote || ""}
                              placeholder="Other..."
                              onChange={(e) => inputActions.onChange(e)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddCustomDietaryItem();
                                }
                              }}
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={handleAddCustomDietaryItem}
                            >
                              Add
                            </Button>
                          </InputGroup>
                        </div>
                      )}
                    </div>

                    {/* Celebration Notes Section */}
                    <div className="mb-6">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="font-weight-boldest font-size-md mb-0">
                          Celebrating anything?
                        </Form.Label>
                        <Form.Check
                          type="switch"
                          id="celebration-switch"
                          label=""
                          checked={showCelebrations}
                          onChange={() =>
                            setShowCelebrations(!showCelebrations)
                          }
                          className="custom-switch"
                        />
                      </div>
                      {showCelebrations && (
                        <div className="p-3 border rounded bg-light-light animate__animated animate__fadeIn">
                          <div>
                            {CELEBRATION_OPTIONS.map((option) => (
                              <Badge
                                key={option}
                                pill
                                style={
                                  (values.celebration || []).some(
                                    (item) => item.label === option,
                                  )
                                    ? activePillStyle
                                    : pillStyle
                                }
                                onClick={() =>
                                  handleCelebrationPillClick(option)
                                }
                              >
                                {option}
                              </Badge>
                            ))}
                          </div>
                          <InputGroup className="mt-3">
                            <Form.Control
                              name="otherCelebrationNote"
                              value={values.otherCelebrationNote || ""}
                              placeholder="Other occasion..."
                              onChange={(e) => inputActions.onChange(e)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddCustomCelebrationItem();
                                }
                              }}
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={handleAddCustomCelebrationItem}
                            >
                              Add
                            </Button>
                          </InputGroup>
                        </div>
                      )}
                    </div>

                    {((values.dietary && values.dietary.length > 0) ||
                      (values.celebration &&
                        values.celebration.length > 0)) && (
                      <div className="mb-6 p-3 border rounded">
                        {values.dietary && values.dietary.length > 0 && (
                          <>
                            <h6 className="font-weight-bold">
                              Selected Dietary Notes:
                            </h6>
                            {values.dietary.map((item, index) => (
                              <div
                                key={`diet-${index}`}
                                className="d-flex justify-content-between align-items-center py-1"
                              >
                                <span>{item.label}</span>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Button
                                    variant="light"
                                    size="sm"
                                    style={{
                                      padding: "0.25rem 0.6rem",
                                      margin: "0 0.25rem",
                                      lineHeight: 1,
                                    }}
                                    onClick={() =>
                                      handleUpdateDietaryCount(
                                        item.label,
                                        item.count - 1,
                                      )
                                    }
                                  >
                                    -
                                  </Button>
                                  <span
                                    style={{
                                      minWidth: "20px",
                                      textAlign: "center",
                                      fontWeight: "bold",
                                      margin: "0 0.25rem",
                                    }}
                                  >
                                    {item.count}
                                  </span>
                                  <Button
                                    variant="light"
                                    size="sm"
                                    style={{
                                      padding: "0.25rem 0.6rem",
                                      margin: "0 0.25rem",
                                      lineHeight: 1,
                                    }}
                                    onClick={() =>
                                      handleUpdateDietaryCount(
                                        item.label,
                                        item.count + 1,
                                      )
                                    }
                                  >
                                    +
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                        {values.celebration &&
                          values.celebration.length > 0 && (
                            <>
                              <h6 className="font-weight-bold mt-3">
                                Selected Celebrations:
                              </h6>
                              {values.celebration.map((item, index) => (
                                <div
                                  key={`celeb-${index}`}
                                  className="d-flex justify-content-between align-items-center py-1"
                                >
                                  <span>{item.label}</span>
                                  <Button
                                    variant="light"
                                    size="sm"
                                    className="py-0 px-2"
                                    onClick={() =>
                                      handleCelebrationPillClick(item.label)
                                    }
                                  >
                                    &times;
                                  </Button>
                                </div>
                              ))}
                            </>
                          )}
                      </div>
                    )}
                  </Form>

                  {/* Action Buttons */}
                  <div className="mt-8 summary-box animate__animated animate__fadeIn">
                    {selectedTable?.isExclusive &&
                      uniqueExperiences.filter((exp) => exp.isExclusive)
                        .length > 0 && (
                        <>
                          <Form.Group
                            controlId="showExclusiveExperiences"
                            className="mb-0"
                          >
                            <FormCheck
                              custom
                              type="checkbox"
                              id="showExclusiveExperiences"
                              checked={showExclusiveExperiences}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setShowExclusiveExperiences(isChecked);
                                if (isChecked) {
                                  const exclusiveExperiences =
                                    uniqueExperiences.filter(
                                      (exp) => exp.isExclusive,
                                    );
                                  if (exclusiveExperiences.length === 1) {
                                    handleExclusiveExperiencePillClick(
                                      exclusiveExperiences[0]._id,
                                    );
                                  }
                                } else {
                                  handlePillClick("experience", null);
                                }
                              }}
                              label="Set to exclusive experience?"
                              className="font-weight-bold purple-checkbox"
                            />
                          </Form.Group>
                          {showExclusiveExperiences && (
                            <div className="mt-3 animate__animated animate__fadeIn">
                              <Form.Group>
                                <Form.Label className="font-weight-bold text-muted font-size-sm">
                                  Exclusive Experiences
                                </Form.Label>
                                <div className="d-flex flex-wrap">
                                  {uniqueExperiences
                                    .filter((exp) => exp.isExclusive)
                                    .map((exp) => {
                                      const isSelected =
                                        values.experience === exp._id;
                                      return (
                                        <OverlayTrigger
                                          key={exp._id}
                                          placement="top"
                                          overlay={
                                            <Tooltip
                                              id={`tooltip-exclusive-exp-${exp._id}`}
                                            >
                                              Exclusive experience is limited to
                                              1 party
                                            </Tooltip>
                                          }
                                        >
                                          <Badge
                                            pill
                                            style={
                                              isSelected
                                                ? activePillStyle
                                                : pillStyle
                                            }
                                            onClick={() =>
                                              handleExclusiveExperiencePillClick(
                                                exp._id,
                                              )
                                            }
                                          >
                                            {exp.name}
                                          </Badge>
                                        </OverlayTrigger>
                                      );
                                    })}
                                </div>
                              </Form.Group>
                            </div>
                          )}
                        </>
                      )}

                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="font-weight-bold text-muted font-size-sm">
                        Experience:
                      </span>
                      <span className="font-weight-bold text-dark-75">
                        {toPesoAmount(experienceSubtotal)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="font-weight-bold text-muted font-size-xs">
                        {selectedExperienceDetails?.name || "Experience"} (
                        {toPesoAmount(selectedExperienceDetails?.price || 0)}) x{" "}
                        {values.party_size}
                      </span>
                    </div>

                    {(values.additionals || []).length > 0 && (
                      <>
                        {(values.additionals || []).map((item, index) => (
                          <div
                            key={`addon-summary-${index}`}
                            className="d-flex justify-content-between align-items-center mb-1"
                            style={{ minWidth: 0 }}
                          >
                            <span
                              className="font-weight-bold text-muted font-size-sm d-flex align-items-center mr-2"
                              style={{ minWidth: 0, overflow: "hidden" }}
                            >
                              <i
                                className="fas fa-plus-circle mr-1 flex-shrink-0"
                                style={{
                                  color: RESERVATION_COLOR,
                                  fontSize: "0.7rem",
                                }}
                              ></i>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip
                                    id={`tooltip-addon-summary-${index}`}
                                  >
                                    {item.label}
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="text-truncate d-inline-block"
                                  style={{ cursor: "pointer" }}
                                  title={item.label}
                                >
                                  {item.label}
                                </span>
                              </OverlayTrigger>
                            </span>
                            <span className="font-weight-bold text-dark-75 flex-shrink-0">
                              {toPesoAmount(item.amount)}
                            </span>
                          </div>
                        ))}
                        <hr
                          className="my-2"
                          style={{ borderColor: `${RESERVATION_COLOR}20` }}
                        />
                      </>
                    )}

                    {isV2 && (
                      <div className="mt-2 mb-2">
                        <FormCheck
                          custom
                          type="checkbox"
                          id="hasCustomAmount"
                          checked={hasCustomAmount}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handlePillClick(
                                "customAmount",
                                String(computedTotalAmount),
                              );
                            } else {
                              handlePillClick("customAmount", "");
                            }
                          }}
                          label="Custom price"
                          className="font-weight-bold"
                        />
                        {hasCustomAmount && (
                          <>
                            <InputGroup
                              className="mt-2"
                              style={{ maxWidth: 220 }}
                            >
                              <InputGroup.Prepend>
                                <InputGroup.Text>₱</InputGroup.Text>
                              </InputGroup.Prepend>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                name="customAmount"
                                value={values.customAmount}
                                onChange={(e) => inputActions.onChange(e)}
                              />
                            </InputGroup>
                            <div className="text-muted font-size-xs mt-1">
                              {selected_reservation
                                ? `New total for booking. Already paid ${toPesoAmount(
                                    paidAmount,
                                  )} — the guest is charged the difference. ${
                                    totalAmount < paidAmount
                                      ? "No refund will be issued."
                                      : ""
                                  }`
                                : "Total amount for this booking."}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-1 mt-1">
                      <span className="font-weight-bold text-muted">
                        Grand Total:
                      </span>
                      <span
                        className="font-weight-boldest font-size-h3"
                        style={{ color: RESERVATION_COLOR }}
                      >
                        {toPesoAmount(totalAmount)}
                      </span>
                    </div>

                    <hr
                      className="my-2"
                      style={{ borderColor: `${RESERVATION_COLOR}20` }}
                    />
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="font-weight-bold text-muted font-size-sm">
                        Paid:
                      </span>
                      <span
                        className="font-weight-bold font-size-sm"
                        style={{
                          color: paidAmount > 0 ? "#15803d" : "#3F4254",
                        }}
                      >
                        {toPesoAmount(paidAmount)}
                      </span>
                    </div>
                    {unpaidTotal > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="font-weight-bold text-muted font-size-sm">
                          Unpaid:
                        </span>
                        <span
                          className="font-weight-boldest font-size-sm"
                          style={{
                            color: "#FFA800",
                          }}
                        >
                          {toPesoAmount(unpaidTotal)}
                        </span>
                      </div>
                    )}

                    {selected_reservation?.payment?.status !== "paid" && (
                      <Form.Group controlId="sendPaymentEmail" className="mt-4">
                        <FormCheck
                          custom
                          type="checkbox"
                          id="withPaymentLink"
                          checked={withPaymentLink}
                          onChange={handlewithPaymentLinkChange}
                          label={
                            selected_reservation?.payment?.status === "pending"
                              ? "Payment link sent"
                              : "Send payment link to customer"
                          }
                          className="font-weight-bold purple-checkbox"
                          disabled={
                            selected_reservation?.payment?.status === "pending"
                          }
                        />
                      </Form.Group>
                    )}
                    {isV2 &&
                      selected_reservation &&
                      hasCustomAmount &&
                      totalAmount > paidAmount && (
                        <Form.Group controlId="payInPerson" className="mt-2">
                          <FormCheck
                            custom
                            type="checkbox"
                            id="payInPerson"
                            checked={Boolean(values.payInPerson)}
                            onChange={(e) =>
                              handlePillClick("payInPerson", e.target.checked)
                            }
                            label="Collect in person instead of payment link"
                            className="font-weight-bold"
                          />
                        </Form.Group>
                      )}
                  </div>
                  {selected_reservation?.payment?.status === "pending" ? (
                    <div
                      className="mt-8 rounded-lg overflow-hidden"
                      style={{
                        border: "1px solid #f0a500",
                        boxShadow: "0 4px 20px rgba(240, 165, 0, 0.15)",
                      }}
                    >
                      {/* Header strip */}
                      <div
                        style={{
                          background: "#ff9800",
                          padding: "10px 16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#fff",
                            display: "inline-block",
                            boxShadow: "0 0 0 3px rgba(255,255,255,0.4)",
                            animation: "pulse-dot 1.8s ease-in-out infinite",
                          }}
                        />
                        <span
                          className="font-weight-boldest text-white font-size-sm text-uppercase"
                          style={{ letterSpacing: "0.8px" }}
                        >
                          Awaiting Payment
                        </span>
                        <span
                          className="ml-auto badge"
                          style={{
                            backgroundColor: "rgba(255,255,255,0.25)",
                            color: "#fff",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            padding: "3px 8px",
                            borderRadius: 20,
                          }}
                        >
                          PENDING
                        </span>
                      </div>

                      {/* Body */}
                      <div
                        style={{
                          backgroundColor: "#fffdf5",
                          padding: "16px",
                        }}
                      >
                        {/* Message */}
                        <div
                          className="d-flex align-items-start p-3 rounded mb-4"
                          style={{
                            backgroundColor: "rgba(245,124,0,0.08)",
                            border: "1px dashed #f5a623",
                          }}
                        >
                          <i
                            className="fas fa-envelope-open-text mr-3 mt-1"
                            style={{ color: "#f57c00", fontSize: "1.1rem" }}
                          />
                          <div className="text-left">
                            <div
                              className="font-weight-boldest"
                              style={{ color: "#e65100", fontSize: "0.85rem" }}
                            >
                              Payment link has been sent
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.78rem" }}
                            >
                              The customer should complete payment via the link
                              in their email. Resend if they haven't received
                              it.
                            </div>
                          </div>
                        </div>

                        {/* Copy Payment Link Button */}
                        <button
                          type="button"
                          onClick={handleCopyPaymentLink}
                          disabled={!paymentLink}
                          title={paymentLink || "Payment link unavailable"}
                          style={{
                            width: "100%",
                            padding: "10px 0",
                            borderRadius: 8,
                            border: copiedPaymentLink
                              ? "1.5px solid #28a745"
                              : "1.5px solid #ff9800",
                            background: copiedPaymentLink ? "#e8f5e9" : "#fff",
                            color: copiedPaymentLink ? "#2e7d32" : "#e65100",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            letterSpacing: "0.5px",
                            cursor: !paymentLink ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            boxShadow: "0 2px 6px rgba(245,124,0,0.12)",
                            transition: "all 0.2s ease",
                            marginBottom: "10px",
                          }}
                        >
                          <i
                            className={
                              copiedPaymentLink ? "fas fa-check" : "fas fa-copy"
                            }
                            style={{
                              color: copiedPaymentLink ? "#2e7d32" : "#e65100",
                              fontSize: "0.95rem",
                            }}
                          />
                          {copiedPaymentLink
                            ? "Payment Link Copied!"
                            : "Copy Payment Link"}
                        </button>

                        {/* Resend Button */}
                        <button
                          type="button"
                          onClick={handleResendEmail}
                          disabled={isAdding}
                          style={{
                            width: "100%",
                            padding: "10px 0",
                            borderRadius: 8,
                            border: "none",
                            background: isAdding ? "#e0e0e0" : "#ff9800",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            letterSpacing: "0.5px",
                            cursor: isAdding ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            boxShadow: isAdding
                              ? "none"
                              : "0 4px 12px rgba(245,124,0,0.35)",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {isAdding ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderWidth: 2,
                                }}
                              />
                              Resending...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane" color="#fff" />
                              Resend Payment Link
                            </>
                          )}
                        </button>
                      </div>

                      <style>{`
                        @keyframes pulse-dot {
                          0%, 100% { opacity: 1; transform: scale(1); }
                          50% { opacity: 0.6; transform: scale(1.3); }
                        }
                      `}</style>
                    </div>
                  ) : null}

                  <div className="mt-6">
                    <Button
                      className="btn btn-primary font-weight-boldest px-8 py-3 w-100 shadow-sm"
                      size="lg"
                      onClick={handleCreateReservation}
                      style={{
                        backgroundColor: RESERVATION_COLOR,
                        borderColor: RESERVATION_COLOR,
                      }}
                      disabled={isAdding || isDeleting || isFormInvalid}
                    >
                      {selected_reservation ? (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          UPDATE RESERVATION
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle mr-2"></i>
                          CONFIRM BOOKING
                        </>
                      )}
                    </Button>

                    {selected_reservation && (
                      <Button
                        variant="light-danger"
                        className="btn btn-light-danger font-weight-boldest px-8 py-3 w-100 mt-3"
                        onClick={handleCancelReservation}
                        disabled={isAdding || isDeleting}
                      >
                        <i className="fas fa-trash-alt mr-2"></i>
                        CANCEL RESERVATION
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Calendar Modal */}
        <Modal
          show={showCalendarModal}
          onHide={() => setShowCalendarModal(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100">
              <div className="d-flex align-items-center">
                <i
                  className="fas fa-calendar-alt mr-3"
                  style={{ color: RESERVATION_COLOR }}
                ></i>
                <div>
                  <h4 className="font-weight-boldest text-dark mb-1">
                    Select Reservation Date
                  </h4>
                  <p className="text-muted font-size-sm mb-0">
                    Click on any date to select it for your reservation
                  </p>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-4">
            <style>
              {`
              .calendar-modal-selected-date {
                background-color: ${RESERVATION_COLOR} !important;
                color: white !important;
                border: 3px solid ${RESERVATION_COLOR} !important;
                font-weight: bold;
                border-radius: 4px;
              }
              .calendar-modal-selected-date .fc-daygrid-day-number {
                color: white !important;
              }
              .fc .fc-button-primary {
                background-color: ${RESERVATION_COLOR} !important;
                border-color: ${RESERVATION_COLOR} !important;
              }
              .fc .fc-button-primary:hover {
                background-color: ${RESERVATION_COLOR}90 !important;
              }
              .fc-daygrid-day:hover {
                background-color: ${RESERVATION_COLOR}90 !important;
                cursor: pointer;
              }
            `}
            </style>
            <div className="alert alert-light-primary border-0 d-flex align-items-center mb-4">
              <i
                className="fas fa-info-circle mr-3"
                style={{ color: RESERVATION_COLOR, fontSize: "1.5rem" }}
              ></i>
              <div>
                <strong>Click on a date</strong> to select it for your
                reservation. Existing reservations are shown on the calendar.
              </div>
            </div>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={values.reservation_date}
              validRange={{ start: new Date() }}
              events={fetchCalendarReservations}
              dateClick={handleCalendarDateClick}
              height="auto"
              dayCellClassNames={(arg) => {
                const cellDate = formatDateForInput(arg.date);
                return cellDate === values.reservation_date
                  ? ["calendar-modal-selected-date"]
                  : [];
              }}
              titleFormat={{ month: "long", year: "numeric" }}
              dayMaxEvents={3}
              dayCellContent={dayHeaderContent}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "",
              }}
            />
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default SmartReservationForm;
